import { BlobParsedStatus, Blob, File as ConsigliFile } from '@consigli/types';
import { StatusVariant } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, Badge, TableCell } from '@/components';

import { useDocumentsContext } from '../context/documents-context';

const PRIMARY_REFERENCE = 'primary_reference';
const BID_LEVELLING_REFERENCE = 'bid_levelling';

const getReferenceLabel = (blob: Blob): string | undefined => {
  const reference = (blob.metadata as { reference?: string } | undefined)?.reference;
  if (typeof reference !== 'string' || reference.length === 0) return undefined;
  if (reference === PRIMARY_REFERENCE) return 'Tender / RFP';
  if (reference === BID_LEVELLING_REFERENCE) return 'Leveling template';
  return reference;
};

// Define a union type for the possible row data
type DocumentRow = Blob | (ConsigliFile & { [key: string]: unknown });

const ActionCell = ({ item }: { item: DocumentRow }) => {
  const { isDeleting, handleViewDetails, handleEditFilename, handleDownload, handleDelete } =
    useDocumentsContext();

  const actions = [
    {
      label: 'View Details',
      onClick: () => handleViewDetails(item),
    },
    {
      label: 'Edit filename',
      onClick: () => handleEditFilename(item),
    },
    {
      label: 'Download',
      onClick: () => handleDownload(item),
    },
    {
      label: 'Delete',
      onClick: () => handleDelete(item),
      disabled: isDeleting,
      destructive: true,
    },
  ];

  return <ActionButton icon={<BsThreeDots />} actions={actions} />;
};

export const useTableConfig = (
  { isBidReview }: { isBidReview: boolean } = { isBidReview: false },
): ColumnDef<DocumentRow, unknown>[] => {
  return useMemo(
    () => [
      {
        header: 'Name',
        accessorFn: (row: DocumentRow) =>
          'name' in row ? row.name : (row as ConsigliFile).filename,
        cell: ({ row }) => (
          <TableCell>
            {'name' in row.original
              ? (row.original as Blob).name
              : (row.original as ConsigliFile).filename}
          </TableCell>
        ),
        enableSorting: false,
      },
      {
        header: 'File Type',
        accessorFn: (row: DocumentRow) =>
          'extension' in row ? (row as Blob).extension : (row as ConsigliFile).filenameExtension,
        cell: ({ row }) => {
          const extension =
            'extension' in row.original
              ? (row.original as Blob).extension
              : (row.original as ConsigliFile).filenameExtension;

          return (
            <TableCell>
              <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                {extension?.toUpperCase() || 'UNKNOWN'}
              </span>
            </TableCell>
          );
        },
        enableSorting: false,
      },
      ...(isBidReview
        ? [
            {
              header: 'Type',
              accessorFn: (row: DocumentRow) =>
                'metadata' in row ? getReferenceLabel(row as Blob) : undefined,
              cell: ({ row }: { row: { original: DocumentRow } }) => {
                if (!('metadata' in row.original)) return <TableCell>—</TableCell>;
                const label = getReferenceLabel(row.original as Blob);
                if (!label) return <TableCell>—</TableCell>;
                return (
                  <TableCell>
                    <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      {label}
                    </span>
                  </TableCell>
                );
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        header: 'Status',
        accessorFn: (row: DocumentRow) =>
          'parsedStatus' in row ? (row as Blob).parsedStatus : (row as ConsigliFile).uploadStatus,
        cell: ({ row }) => {
          const item = row.original;
          if ('parsedStatus' in item) {
            switch ((item as Blob).parsedStatus) {
              case BlobParsedStatus.PARSED:
                return <Badge variant={StatusVariant.SUCCESS} text="Parsed" />;
              case BlobParsedStatus.DUPLICATE:
                return <Badge variant={StatusVariant.ERROR} text="Duplicate" />;
              case BlobParsedStatus.UNPARSED:
                return <Badge variant={StatusVariant.WARNING} text="Unparsed" />;
              case BlobParsedStatus.PENDING:
                return <Badge variant={StatusVariant.WARNING} text="Pending" />;
              case BlobParsedStatus.UPLOADING:
                return <Badge variant={StatusVariant.WARNING} text="Uploading" />;
              case BlobParsedStatus.UPLOADED:
                return <Badge variant={StatusVariant.INFO} text="Uploaded" />;
              case BlobParsedStatus.UPLOAD_FAILED:
                return <Badge variant={StatusVariant.ERROR} text="Upload Failed" />;
              case BlobParsedStatus.PARSING_FAILED:
                return <Badge variant={StatusVariant.ERROR} text="Parsing Failed" />;
              default:
                return <Badge variant={StatusVariant.WARNING} text="Unknown" />;
            }
          } else {
            return <Badge variant={StatusVariant.INFO} text="File" />;
          }
        },
        enableSorting: false,
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => <ActionCell item={row.original as DocumentRow} />,
        enableSorting: false,
      },
    ],
    [isBidReview],
  );
};
